/**
 * Text normalization utilities: apostrophe normalization, diacritics stripping,
 * and URL/email pattern preservation.
 */

/**
 * Regex to match URLs (http, https, ftp, file protocols).
 * Matches protocol through end of URL (stops at whitespace or common punctuation at end).
 */
export const URL_REGEX = /(?:https?|ftp|file):\/\/[^\s<>"')\]]+/gi;

/**
 * Regex to match email addresses.
 * Simple pattern: word characters, dots, hyphens, plus before @, domain after.
 * Quantifiers are bounded per RFC 5321 limits to prevent ReDoS.
 */
export const EMAIL_REGEX = /[\w.%+-]{1,64}@(?:[a-z0-9-]{1,63}\.){1,10}[a-z]{2,63}/gi;

/**
 * Regex to match single letters each followed by a period ("e.g.", "i.e.",
 * "U.S.A."), including the final period, so it is not read as a sentence end.
 */
export const LETTER_ABBREVIATION_REGEX = /(?<![\p{L}\p{N}_.])(?:\p{L}\.){2,}(?![\p{L}\p{N}_])/gu;

/**
 * Regex to match letters/digits joined by '.' with no whitespace: domains
 * ("f-droid.org"), filenames ("ansi2html.py"), names ("node.js"), versions
 * ("1.2.3"). Includes an optional path after the token. A trailing period is
 * left out, so "see node.js." still ends a sentence.
 */
const DOTTED_LABEL = String.raw`[\p{L}\p{N}_][-\p{L}\p{N}_]*`;
export const DOTTED_TOKEN_REGEX = new RegExp(
  String.raw`(?<![\p{L}\p{N}_.-])${DOTTED_LABEL}(?:\.${DOTTED_LABEL})+(?![\p{L}\p{N}_])(?:\/[^\s<>"')\]]*)?`,
  'gu'
);

/**
 * Extracts URLs, emails and dotted tokens from text, replacing them with placeholders.
 * Returns the modified text and a map to restore originals.
 * Placeholders use non-alphanumeric characters to avoid being split by word regex.
 */
export function extractPreservedPatterns(text: string): {
  preserved: Map<string, string>;
  text: string;
} {
  const preserved = new Map<string, string>();
  let counter = 0;
  let result = text;

  // Short-circuit each regex when its trigger pattern isn't present.
  // Most text has no URLs, emails, or bare domains.

  // Replace URLs first (they may contain email-like patterns)
  // Use \x00 (null) and \x01 (SOH) to create non-word placeholders
  if (text.includes('://')) {
    result = result.replaceAll(URL_REGEX, (match) => {
      const placeholder = `\u0000\u0001${counter++}\u0001\u0000`;
      preserved.set(placeholder, match);
      return placeholder;
    });
  }

  // Replace emails
  if (result.includes('@')) {
    result = result.replaceAll(EMAIL_REGEX, (match) => {
      const placeholder = `\u0000\u0001${counter++}\u0001\u0000`;
      preserved.set(placeholder, match);
      return placeholder;
    });
  }

  // Replace abbreviations, then other dotted tokens (after URLs and emails to
  // avoid double-matching)
  if (result.includes('.')) {
    for (const regex of [LETTER_ABBREVIATION_REGEX, DOTTED_TOKEN_REGEX]) {
      result = result.replaceAll(regex, (match) => {
        const placeholder = `\u0000\u0001${counter++}\u0001\u0000`;
        preserved.set(placeholder, match);
        return placeholder;
      });
    }
  }

  return { preserved, text: result };
}

/**
 * Replaces the placeholders from extractPreservedPatterns in text with their originals.
 */
export function restorePreservedPatterns(text: string, preserved: Map<string, string>): string {
  let result = text;
  for (const [placeholder, original] of preserved) {
    result = result.replace(placeholder, original);
  }
  return result;
}

// Pre-compiled regex patterns (avoid per-call RegExp object creation)
const FANCY_APOSTROPHE = /[\u2018\u2019\u02BC]/g;
const COMBINING_MARKS = /[\u0300-\u036F]/g;

/**
 * Normalizes various apostrophe characters to the standard straight apostrophe.
 * Handles: ' (U+2019 right single quotation mark), ' (U+2018 left), ʼ (U+02BC modifier letter)
 */
export function normalizeApostrophes(text: string): string {
  if (!text.includes('\u2018') && !text.includes('\u2019') && !text.includes('\u02BC')) {
    return text;
  }
  return text.replaceAll(FANCY_APOSTROPHE, "'");
}

/**
 * Strips diacritics/accents from text, preserving base letters.
 * Converts résumé→resume, naïve→naive, cliché→cliche, café→cafe.
 * Uses Unicode NFD decomposition to separate base letters from combining marks.
 */
export function stripDiacritics(text: string): string {
  return text.normalize('NFD').replaceAll(COMBINING_MARKS, '');
}
